import React, { useState } from 'react';
import { Phone, Mail, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { cateringMenuItems } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderOnline = () => {
  const { toast } = useToast();
  const [orderInfo, setOrderInfo] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventTime: '',
    guestCount: '',
    selectedItems: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setOrderInfo({
      ...orderInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/order-request`, orderInfo);
      
      toast({
        title: "Order Request Submitted!",
        description: "We'll contact you shortly to discuss your catering needs and provide pricing.",
      });
      
      // Reset form
      setOrderInfo({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        eventTime: '',
        guestCount: '',
        selectedItems: '',
        specialRequests: ''
      });
    } catch (error) {
      console.error('Error submitting order:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try calling us instead.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Breakfast', 'Lunch', 'Supper', 'Appetizers'];

  const filteredItems = activeCategory === 'All' 
    ? cateringMenuItems 
    : cateringMenuItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-nanny-cream">
      {/* Hero Section */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nanny-dark mb-6 heading-font">Request Catering</h1>
            <p className="text-xl text-gray-700 mb-4">
              Browse our catering options below and submit a request. We'll contact you with pricing and details!
            </p>
            <div className="flex items-center justify-center space-x-4 text-nanny-maroon">
              <Phone className="h-5 w-5" />
              <span>Prefer to call? <a href="tel:731-267-9884" className="font-bold hover:underline">731-267-9884</a></span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            
            {/* Catering Options Display */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-nanny-dark mb-8 text-center heading-font">Our Catering Options</h2>
              
              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {categories.map((category) => (
                  <Badge
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`cursor-pointer px-6 py-2 text-base transition-all duration-300 ${
                      activeCategory === category
                        ? 'bg-nanny-maroon text-white hover:bg-nanny-dark'
                        : 'bg-white text-nanny-maroon border-2 border-nanny-maroon hover:bg-nanny-cream'
                    }`}
                  >
                    {category}
                  </Badge>
                ))}
              </div>

              {/* Menu Items Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => (
                  <Card key={item.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-nanny-maroon overflow-hidden">
                    {item.image && (
                      <div className="h-56 overflow-hidden">
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <Badge className="mb-3 bg-nanny-cream text-nanny-maroon">{item.category}</Badge>
                      <h3 className="text-xl font-bold text-nanny-dark mb-2">{item.name}</h3>
                      <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                      <p className="text-sm text-gray-500">Serves: {item.servings}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Order Request Form */}
            <div className="max-w-3xl mx-auto">
              <Card className="border-2 border-nanny-maroon shadow-xl">
                <CardHeader className="bg-nanny-maroon text-white">
                  <CardTitle className="text-2xl text-center">Submit Your Catering Request</CardTitle>
                  <p className="text-center text-nanny-cream text-sm mt-2">
                    Fill out the form below and we'll contact you with pricing and availability
                  </p>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={orderInfo.name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone *
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={orderInfo.phone}
                          onChange={handleInputChange}
                          placeholder="731-267-9884"
                          className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={orderInfo.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label htmlFor="eventDate" className="block text-sm font-semibold text-gray-700 mb-2">
                          Event Date *
                        </label>
                        <Input
                          id="eventDate"
                          name="eventDate"
                          type="date"
                          required
                          value={orderInfo.eventDate}
                          onChange={handleInputChange}
                          className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                        />
                      </div>
                      <div>
                        <label htmlFor="eventTime" className="block text-sm font-semibold text-gray-700 mb-2">
                          Event Time *
                        </label>
                        <Input
                          id="eventTime"
                          name="eventTime"
                          type="time"
                          required
                          value={orderInfo.eventTime}
                          onChange={handleInputChange}
                          className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                        />
                      </div>
                      <div>
                        <label htmlFor="guestCount" className="block text-sm font-semibold text-gray-700 mb-2">
                          Guest Count
                        </label>
                        <Input
                          id="guestCount"
                          name="guestCount"
                          type="number"
                          value={orderInfo.guestCount}
                          onChange={handleInputChange}
                          placeholder="# of guests"
                          className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="selectedItems" className="block text-sm font-semibold text-gray-700 mb-2">
                        Items You're Interested In
                      </label>
                      <Textarea
                        id="selectedItems"
                        name="selectedItems"
                        value={orderInfo.selectedItems}
                        onChange={handleInputChange}
                        placeholder="List the items you'd like to order (e.g., Breakfast Croissant Tray, Wrap Platter, Cookie Tray)"
                        rows={3}
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <div>
                      <label htmlFor="specialRequests" className="block text-sm font-semibold text-gray-700 mb-2">
                        Special Requests or Dietary Restrictions
                      </label>
                      <Textarea
                        id="specialRequests"
                        name="specialRequests"
                        value={orderInfo.specialRequests}
                        onChange={handleInputChange}
                        placeholder="Any special requests, allergies, or dietary restrictions we should know about?"
                        rows={4}
                        className="border-gray-300 focus:border-nanny-maroon focus:ring-nanny-maroon"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-nanny-maroon hover:bg-nanny-dark text-white py-6 text-lg font-semibold"
                    >
                      <Calendar className="mr-2 h-5 w-5" />
                      {isSubmitting ? 'Submitting...' : 'Submit Catering Request'}
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      We'll contact you within 24 hours to discuss pricing and finalize your order
                    </p>
                  </form>
                </CardContent>
              </Card>

              {/* Quick Contact */}
              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">Need help right away?</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:731-267-9884">
                    <Button className="bg-nanny-maroon hover:bg-nanny-dark text-white">
                      <Phone className="mr-2 h-5 w-5" />
                      Call 731-267-9884
                    </Button>
                  </a>
                  <a href="https://www.facebook.com/profile.php?id=100068874377725" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-2 border-nanny-maroon text-nanny-maroon hover:bg-nanny-maroon hover:text-white">
                      Message on Facebook
                    </Button>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderOnline;
